import React from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import update from 'immutability-helper'
import { trim } from 'lodash'
import FormField from 'components/FormField'
import AddressFormError from 'errors/AddressFormError'
import { useAPI } from 'contexts/APIProvider'
import { useSnackBarAlert } from 'contexts/SnackBarAlertProvider'
import { joinAddressParts } from 'logic/helpers'
import { AddressForm } from 'types/interfaces'

interface ManualAddressFieldsProps {
  form: AddressForm
  error?: AddressFormError
  setForm: (form: AddressForm) => void
  clearError: (name: string) => void
}

const ManualAddressFields: React.FC<ManualAddressFieldsProps> = ({ form, error, setForm, clearError }) => {
  const { api } = useAPI()
  const { showAlert } = useSnackBarAlert()

  // Keep `address` joined from the individual fields
  const updateManualAddress = (name: string, value: any) => {
    const updated = update(form, { [name]: { $set: value } })
    setForm(update(updated, { address: { $set: joinAddressParts(updated) } }))
    clearError(name)
  }

  const prepopulateCoordinates = async () => {
    try {
      if (!form.postcode) return
      const coordinates = await api.getCoordinatesFromPostcode(form.postcode)
      setForm(update(form, {
        latitude: { $set: coordinates.latitude },
        longitude: { $set: coordinates.longitude },
      }))
    } catch (e) {
      showAlert('error', 'Error getting coordinates from postcode')
      console.error('error getting coordinates from postcode', e)
    }
  }

  return (
    <React.Fragment>
      <FormField
        name='line1'
        label='Address Line 1'
        value={form.line1 ?? ''}
        onChange={e => updateManualAddress('line1', trim(e.target.value ?? ''))}
        errors={error?.line1}
      />
      <FormField
        name='line2'
        label='Address Line 2'
        value={form.line2 ?? ''}
        onChange={e => updateManualAddress('line2', trim(e.target.value ?? ''))}
        errors={error?.line2}
      />
      <FormField
        name='city'
        label='City'
        value={form.city ?? ''}
        onChange={e => updateManualAddress('city', trim(e.target.value ?? ''))}
        errors={error?.city}
      />
      <FormField
        name='county'
        label='County'
        value={form.county ?? ''}
        onChange={e => updateManualAddress('county', trim(e.target.value ?? ''))}
        errors={error?.county}
      />
      <FormField
        name='country'
        label='Country'
        value={form.country ?? ''}
        onChange={e => updateManualAddress('country', trim(e.target.value ?? ''))}
        errors={error?.country}
      />
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
        <FormField
          name='postcode'
          label='Postcode'
          value={form.postcode ?? ''}
          onChange={e => updateManualAddress('postcode', trim(e.target.value ?? ''))}
          errors={error?.postcode}
          sx={{ flex: 1 }}
        />
        <Button variant='outlined' color='primary' onClick={prepopulateCoordinates}>
          Get Coordinates
        </Button>
      </Box>
      <FormField
        name='latitude'
        label='Latitude'
        type='number'
        value={form.latitude ?? ''}
        onChange={e => updateManualAddress('latitude', e.target.value ? Number(e.target.value) : undefined)}
        errors={error?.latitude}
      />
      <FormField
        name='longitude'
        label='Longitude'
        type='number'
        value={form.longitude ?? ''}
        onChange={e => updateManualAddress('longitude', e.target.value ? Number(e.target.value) : undefined)}
        errors={error?.longitude}
      />
    </React.Fragment>
  )
}

export default ManualAddressFields
